import React from 'react';
import { BodyText } from '@minecraft.atoms';
import UncontrolledDropdown from '@minecraft.uncontrolled-dropdown';
import DropdownMenu from '@minecraft.dropdown-menu';
import DropdownItem from '@minecraft.dropdown-item';
import DropdownToggle from '@minecraft.dropdown-toggle';
import { WindowWidthContext } from '@minecraft.utils';
import styled from 'styled-components';
import { DropdownItemProps, OptionalProps } from './types';

const buildOptionalProps = ({ 'data-testid': testIdProp, qaId, className }: OptionalProps) => {
  return {
    ...(typeof testIdProp === 'string' && testIdProp ? { 'data-testid': testIdProp } : {}),
    ...(typeof qaId === 'string' && qaId ? { 'data-qa-id': qaId } : {}),
    ...(typeof className === 'string' && className ? { className } : {}),
  };
};

interface BaseDropdownButtonProps<ToggleProps extends Record<string, any>> extends OptionalProps {
  block?: boolean;
  /** use "body" to have the menu attach to the document body instead of being set inside the dropdown
   * this can help with overflow issues inside containers with smaller sizes where the menu might be cut off
   * in most cases you won't need this though
   */
  menuContainer?: 'body' | undefined;
  menuAlign?: 'right' | 'left';
  menuChildren?: React.ReactNode;
  menuItemsProps: DropdownItemProps[];
  mobileBlock?: boolean;
  showCaret?: boolean;
  toggleChildren?: React.ReactNode;
  ToggleComponent: React.ElementType<ToggleProps>;
  toggleProps?: Partial<ToggleProps>;
}

interface StyledMenuProps {
  $hasStickyProp?: boolean;
}

const StyledDropdownMenu = styled(DropdownMenu)<StyledMenuProps>`
  border-color: ${({ theme }) => theme.designSystem.borderColors.menu};
  // Only apply 0 padding if $hasStickyProp is true
  ${({ $hasStickyProp }) =>
    $hasStickyProp &&
    `
    && {
      padding-top: 0;
      padding-bottom: 0;
    }
  `}
`;

export const BaseDropdownButton = <ToggleProps extends Record<string, any>>({
  'data-testid': dataTestId,
  block,
  className,
  menuAlign,
  menuChildren,
  menuContainer,
  menuItemsProps,
  mobileBlock,
  qaId,
  showCaret,
  toggleChildren,
  ToggleComponent,
  toggleProps,
}: BaseDropdownButtonProps<ToggleProps>) => {
  const isMobile = WindowWidthContext.useIsMobile();
  const qaIdBase = qaId || 'dropdown';
  const testIdBase = dataTestId || 'dropdown';
  const menuItemsWithIds = menuItemsProps.filter(
    (mi) => ['number', 'string'].includes(typeof mi?.id) && !!mi?.id?.toString()
  );
  const hasStickyMenuItem = menuItemsWithIds.some((mi) => mi?.sticky);
  const menuWidth = mobileBlock && isMobile ? { width: '100%' } : {};
  const rightAlignProp = menuAlign === 'right' ? { right: true } : {};

  const ddBlockWidth = block || (mobileBlock && isMobile) ? { width: '100%' } : {};
  const { qaId: toggleQaId, testId: toggleTestId, ...restToggleProps } = toggleProps;

  return (
    <UncontrolledDropdown
      style={{ ...ddBlockWidth }}
      {...buildOptionalProps({ 'data-testid': `${testIdBase}-wrapper`, qaId: `${qaIdBase}-wrapper`, className })}
    >
      <DropdownToggle
        {...restToggleProps}
        {...buildOptionalProps({
          'data-testid': toggleTestId || `${testIdBase}-toggle`,
          qaId: toggleQaId || `${qaIdBase}-toggle`,
        })}
        caret={showCaret}
        tag={ToggleComponent}
      >
        {toggleChildren}
      </DropdownToggle>
      <StyledDropdownMenu
        container={menuContainer}
        $hasStickyProp={hasStickyMenuItem}
        style={{
          ...menuWidth,
          minWidth: menuContainer === 'body' ? 'max-content' : '100%',
          maxHeight: '50vh',
          overflowX: 'auto',
          zIndex: 1051,
        }}
        {...rightAlignProp}
        {...buildOptionalProps({ 'data-testid': `${testIdBase}-menu`, qaId: `${qaIdBase}-menu` })}
      >
        {menuItemsWithIds.map(
          ({
            id,
            label,
            qaId: menuQaId,
            'data-testid': menuTestId,
            className: menuClassName,
            color,
            onClick,
            sticky,
            ...miProps
          }) => (
            <DropdownItem
              key={id}
              $sticky={sticky}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onClick?.(e);
              }}
              {...miProps}
              {...buildOptionalProps({ 'data-testid': menuTestId, qaId: menuQaId, className: menuClassName })}
            >
              <BodyText color={color || 'default'}>{label}</BodyText>
            </DropdownItem>
          )
        )}
        {menuChildren}
      </StyledDropdownMenu>
    </UncontrolledDropdown>
  );
};
